/**
 * AI-0: the stub.
 *
 * The roadmap splits the AI across three phases precisely because a single monolithic
 * AI phase is where ambitious 4X projects die. This is the first and smallest of them,
 * and its job is not to play well. Its job is to EXIST — to make rival civilisations
 * something other than scenery, so that everything built after this has opponents to
 * be tested against. AI-1 (Phase 8) does the strategy; AI-2 (Phase 10) does diplomacy
 * and war.
 *
 * Even so it scores rather than scripts, because the shape matters more than the
 * quality. Every decision below is "give each option a number and take the best one",
 * with the weights in `content/ai.json`. That is the structure AI-1 grows into; a pile
 * of if-statements would have to be thrown away instead.
 *
 * It issues ordinary commands through the same `apply` the player uses. It has no
 * privileged access to anything — no seeing through fog, no free units, no bypassing a
 * rule. When it cheats later it will be because a difficulty setting says so, in data,
 * and not because the code found it convenient.
 */

import { paymentBalance } from '../diplomacy/deals.js';
import type { Content, UnitDef } from '../../../content/schema/index.js';
import { neighborIndices } from '../state/grid.js';
import { resourceAccess, resourceBlocker, type ResourceAccess } from '../resources/access.js';
import { apply, asAutomation } from '../commands/index.js';
import type { GameEvent } from '../commands/types.js';
import { citiesOf, cityIndex, foodIsWasted } from '../cities/index.js';
import { buildableItems, buildBlocker, itemCost } from '../cities/production.js';
import { wonderBlocker } from '../cities/wonders.js';
import { availableGovernments, governmentAppeal, governmentOf, inAnarchy, maxScienceRate } from '../government/index.js';
import { budget, budgetAt, budgetBasis, inDisorder } from '../cities/economy.js';
import { nextInt, type RngState } from '../rng/index.js';
import { indexToOffset, tileCount } from '../state/grid.js';
import { scoreStart, wrappedDistance } from '../state/start.js';
import { NONE, VIS_UNKNOWN, type GameState, type Id, type ProductionItem, type Unit } from '../state/types.js';
import { unitDef, unitIndex, unitsOf } from '../units/index.js';
import { availableTechs, researchScore } from '../research/index.js';
import { canAttack, canDefend, isSiege } from '../combat/index.js';
import { enemiesOf, rivalsOf } from '../diplomacy/index.js';
import { contactsOf } from '../diplomacy/borders.js';
import { weightsFor } from './personality.js';
import { directDeals } from './diplomat.js';
import {
  directDiplomacy,
  bombardBest,
  directAircraft,
  directShip,
  directSoldier,
  directUpgrades,
  garrisonWant,
  overseasTarget,
  planGarrisons,
  arming,
  upgradeBill,
} from './military.js';
import { directCampaign, isArmyType, type Front } from './campaign.js';
import { hasTech } from '../research/knowledge.js';
import { improvementAllowed } from '../units/work.js';
import { canEnter } from '../path/movement.js';

/**
 * Run one civilisation's turn.
 *
 * The active civ is switched for the duration because commands are validated against
 * it — the AI goes through exactly the same gate the player does, which is the whole
 * reason to have a command pipeline at all.
 */
export function takeAiTurn(state: GameState, content: Content, civId: Id): GameEvent[] {
  const events: GameEvent[] = [];
  accessMemo.clear();
  const previous = state.meta.activeCiv;
  state.meta.activeCiv = civId;

  try {
    events.push(...directGovernment(state, content, civId));
    events.push(...directResearch(state, content, civId));
    const rng = state.rng['ai'];
    events.push(...directDiplomacy(state, content, civId, rng));
    events.push(...directDeals(state, content, civId, rng));
    events.push(...directUpgrades(state, content, civId));
    const posts = planGarrisons(state, content, civId);
    const front = directCampaign(state, content, civId, posts);
    // Guns first, then siege engines, then everyone else: the walls are worn down before
    // the assault goes in (ADR-0072).
    const rank = (u: Unit): number => {
      const def = unitDef(content, u);
      // Ships move last, so an army boarding this turn is aboard before they sail.
      return def.bombard ? 0 : isSiege(def) ? 1 : def.domain === 'sea' ? 3 : 2;
    };
    for (const unit of unitsOf(state, civId).sort((a, b) => rank(a) - rank(b) || a.id - b.id)) {
      // A unit can die or change hands mid-turn; the list was taken before it did.
      if (!state.units[unit.id] || unit.owner !== civId) continue;
      events.push(...directUnit(state, content, civId, unit, rng, posts, front));
    }
    for (const city of citiesOf(state, civId)) {
      events.push(...directCity(state, content, city.id, front));
    }
  } finally {
    state.meta.activeCiv = previous;
  }

  return events;
}

// ─── government ────────────────────────────────────────────────────────────────

/**
 * Change government when a better one is worth the anarchy (ADR-0059).
 *
 * Each government's appeal is its value to this personality (governments.json
 * `aiValue`), less a penalty for how much its people mind war while the civilisation is
 * at war. A change must beat the present government by a margin, and not soon after the
 * last one — or a militarist would revolt every time it made peace.
 */
function directGovernment(state: GameState, content: Content, civId: Id): GameEvent[] {
  const civ = state.civs[civId];
  if (!civ || inAnarchy(civ)) return [];
  const ai = weightsFor(content, civ);
  if (state.meta.turn - civ.governmentSince < ai.governmentMinTurns && civ.government !== content.governments.id(content.rules.government.start)) {
    return [];
  }
  const current = governmentAppeal(state, content, civ, governmentOf(content, civ), ai.governmentWarPenalty);
  let best = -1;
  let bestScore = current + ai.governmentSwitchMargin;
  for (const def of availableGovernments(content, civ)) {
    if (def.id === civ.government) continue;
    const score = governmentAppeal(state, content, civ, def, ai.governmentWarPenalty);
    if (score > bestScore) {
      best = def.id;
      bestScore = score;
    }
  }
  if (best < 0) return [];
  const result = apply(state, content, { t: 'ChangeGovernment', government: best });
  return result.ok ? result.value : [];
}

// ─── research ──────────────────────────────────────────────────────────────────

/**
 * Keep the books balanced and the scholars busy.
 *
 * The rate is the highest the treasury can sustain, re-judged every turn as the empire
 * changes. The choice of technology is, for AI-0, the cheapest available once each is
 * weighed by what it unlocks against what it costs (researchScore) — and since AI-1, by
 * what those unlocks are worth to its personality (ADR-0053).
 */
function directResearch(state: GameState, content: Content, civId: Id): GameEvent[] {
  const events: GameEvent[] = [];
  const civ = state.civs[civId];
  if (!civ) return events;

  // The highest rate the books allow — counting a treasury above `upgradeReserve` as money
  // to spend over `treasuryRunway` turns (Phase 13a). Without that the AI kept a positive
  // balance at every rate step and sat on thousands of gold while its research crawled.
  const weights0 = weightsFor(content, civ);
  const spare = Math.max(0, civ.treasury - weights0.upgradeReserve) / Math.max(1, weights0.treasuryRunway);
  // Gold promised under deals is spent before science (ADR-0073): leaving it out is how
  // AI civilisations defaulted a dozen times a game.
  const owing = paymentBalance(state, civId);
  let rate = 0;
  const basis = budgetBasis(state, content, civId);
  for (let r = maxScienceRate(state, content, civId); r >= 0; r -= content.rules.research.rateStep) {
    if (budgetAt(basis, civ.researchRate, r).net + owing + spare >= 0) {
      rate = r;
      break;
    }
  }
  // Save up when there is an army to modernise and nothing to pay for it with.
  const owed = upgradeBill(state, content, civId);
  const weights = weightsFor(content, civ);
  if (owed > 0 && civ.treasury < owed + weights.upgradeReserve) {
    rate = Math.max(0, rate - weights.upgradeSavingSteps * content.rules.research.rateStep);
  }
  if (rate !== civ.researchRate) {
    const result = apply(state, content, { t: 'SetScienceRate', rate });
    if (result.ok) events.push(...result.value);
  }

  if (civ.researching === NONE && civ.researchGoal === NONE) {
    // Cost discounted by what it unlocks (researchScore); ties to the tree's own order.
    const choice = availableTechs(content, civ).sort(
      (a, b) => researchScore(content, a.id, weights) - researchScore(content, b.id, weights) || a.id - b.id,
    )[0];
    if (choice) {
      const result = apply(state, content, { t: 'SetResearch', tech: choice.id });
      if (result.ok) events.push(...result.value);
    }
  }
  return events;
}

// ─── units ─────────────────────────────────────────────────────────────────────

function directUnit(
  state: GameState,
  content: Content,
  civId: Id,
  unit: Unit,
  rng: RngState | undefined,
  posts: ReadonlyMap<Id, Id>,
  front: Front | null,
): GameEvent[] {
  if (unit.movesLeft <= 0) return [];
  const def = unitDef(content, unit);

  // Aircraft fly missions from their cities and never walk anywhere (ADR-0065).
  if (def.domain === 'air') return directAircraft(state, content, civId, unit);

  // Guns that reach fire first, before anyone walks up to the walls (ADR-0064).
  if (def.bombard) {
    const fired = bombardBest(state, content, civId, unit);
    if (fired.length > 0) return fired;
  }

  if (def.domain === 'sea') {
    const naval = directShip(state, content, civId, unit, front);
    if (naval !== null) return naval;
    // A ship that ran out of sea to look at drops anchor; every seaExploreRetry turns it
    // looks again, from wherever the map has grown since (ADR-0072).
    if (unit.orders !== null && !(unit.orders.kind === 'fortify' && state.meta.turn % weightsFor(content, state.civs[civId]).seaExploreRetry === 0)) return [];
    return explore(state, content, civId, unit, rng);
  }

  // Soldiers are re-judged every turn — a war, a city left unguarded, an enemy that
  // walked up beside them all change what they should be doing.
  if (canDefend(def) || canAttack(def)) {
    const military = directSoldier(state, content, civId, unit, posts, front);
    if (military !== null) return military;
  }

  // Anything else already doing something is left to do it. Second-guessing every
  // standing order every turn is how an AI ends up walking in circles.
  if (unit.orders !== null) return [];

  if (def.abilities.includes('found_city')) return settle(state, content, civId, unit);
  if (def.abilities.includes('build_improvement')) return improve(state, content, civId, unit);
  return explore(state, content, civId, unit, rng);
}

/**
 * Found a city here, or walk toward somewhere better.
 *
 * Reuses the player's own start-position scoring, which is the right call twice over:
 * the AI wants the same thing a good opening wants, and a second scoring function
 * would be a second thing to keep correct.
 */
function settle(state: GameState, content: Content, civId: Id, unit: Unit): GameEvent[] {
  const here = unitIndex(state.map, unit);
  const found = apply(state, content, { t: 'FoundCity', unitId: unit.id });
  if (found.ok) return found.value;

  const site = bestSiteNear(state, content, civId, here, unit.id);
  if (site === null || site === here) {
    // Nowhere better and nowhere legal — stop burning turns on it.
    const skip = apply(state, content, { t: 'SkipUnit', unitId: unit.id });
    return skip.ok ? skip.value : [];
  }

  const go = apply(state, content, { t: 'SetGoto', unitId: unit.id, to: site });
  return go.ok ? go.value : [];
}

/**
 * Every tile that could lie within `radius` of `from`, in ascending index order: the rows
 * within reach, and the columns within reach of each (wrapping east–west if the map does).
 * A superset — callers still check the true distance.
 */
function tilesWithin(map: GameState['map'], from: number, radius: number): number[] {
  const width = map.width;
  const col = from % width;
  const row = (from - col) / width;
  const r = Math.ceil(radius) + 1;
  const out = new Set<number>();
  for (let y = Math.max(0, row - r); y <= Math.min(map.height - 1, row + r); y++) {
    for (let dx = -r; dx <= r; dx++) {
      let x = col + dx;
      if (map.wrapX) x = ((x % width) + width) % width;
      else if (x < 0 || x >= width) continue;
      out.add(y * width + x);
    }
  }
  return [...out].sort((a, b) => a - b);
}

/** The best city site the civilisation knows about within a sensible walk. */
function bestSiteNear(state: GameState, content: Content, civId: Id, from: number, settler: Id = NONE, anyWill = false): number | null {
  const ai = weightsFor(content, state.civs[civId]);
  const vision = state.vision[civId];
  const cities = Object.values(state.cities);
  const claimed: number[] = [];
  for (const other of unitsOf(state, civId)) {
    if (other.id === settler || other.orders?.kind !== 'goto') continue;
    if (unitDef(content, other).abilities.includes('found_city')) claimed.push(other.orders.destination);
  }

  // Tiles too near an existing city to found one on, marked once (Phase 15) rather than
  // measured against every city for every candidate. Only cities near enough to matter.
  const spacing = content.rules.cities.minSpacing;
  const crowded = new Set<number>();
  for (const city of cities) {
    const at = cityIndex(state.map, city);
    if (wrappedDistance(state.map, from, at) > ai.settleSearchRadius + spacing) continue;
    for (const t of tilesWithin(state.map, at, spacing)) if (wrappedDistance(state.map, t, at) < spacing) crowded.add(t);
  }

  let best: number | null = null;
  let bestScore = -Infinity;

  // Only tiles within a walk can qualify, so only those are looked at — in index order,
  // as before, so ties still go the same way (Phase 15: this scanned the whole map for
  // every city every turn).
  for (const index of tilesWithin(state.map, from, ai.settleSearchRadius)) {
    if (vision && vision[index] === VIS_UNKNOWN) continue;

    const terrain = content.terrain.get(state.map.terrain[index]!);
    if (terrain.water || !terrain.passable) continue;

    const distance = wrappedDistance(state.map, from, index);
    if (distance > ai.settleSearchRadius) continue;

    // Respect the same spacing rule the command enforces, so the AI does not walk
    // somewhere it will then be refused — and keep clear of where its other settlers
    // are already heading, so two of them do not race for one valley (AI-1).
    let tooClose = crowded.has(index);
    for (const claim of claimed) {
      if (wrappedDistance(state.map, index, claim) < content.rules.cities.minSpacing) {
        tooClose = true;
        break;
      }
    }
    if (tooClose) continue;
    // Ground a rival already holds is not ours to settle.
    const holder = state.map.owner[index]!;
    if (holder !== NONE && holder !== civId) continue;

    // Good ground, discounted by how long it takes to walk there. An excellent site
    // twenty turns away is worse than a decent one next door.
    // Asked only whether there is room at all: the first site that qualifies will do.
    if (anyWill) return index;
    const score = scoreStart(state.map, content, index) - distance * ai.settleDistancePenalty;
    if (score > bestScore) {
      bestScore = score;
      best = index;
    }
  }

  return best;
}

/**
 * Which strategic resources reach where, remembered for the rest of one civilisation's
 * AI turn (Phase 15: it was recomputed for every worker and city). Roads and borders only
 * change between turns; a city founded or taken, a trade or a new technology mid-turn
 * changes the key and forces a fresh answer.
 */
const accessMemo = new Map<string, ResourceAccess>();
export function accessFor(state: GameState, content: Content, civId: Id): ResourceAccess {
  let cities = 0;
  for (const c of Object.values(state.cities)) cities = (cities * 31 + c.id * 7 + c.owner) % 1_000_000_007;
  const key = `${state.meta.turn}|${civId}|${state.civs[civId]?.techs.length ?? 0}|${state.grants.length}|${cities}`;
  let access = accessMemo.get(key);
  if (!access) {
    access = resourceAccess(state, content, civId);
    accessMemo.set(key, access);
  }
  return access;
}

/**
 * Tiles that need a road to join a strategic resource the civilisation lacks to one of
 * its cities: the shortest way over its own or unclaimed land, found breadth-first from
 * each unconnected deposit, at most `MAX_LINK` steps long (ADR-0063).
 */
const MAX_LINK = 10;
export function resourceRoadJobs(state: GameState, content: Content, civId: Id): number[] {
  const access = accessFor(state, content, civId);
  const map = state.map;
  const wanted = access.deposits.filter((d) => !d.connected && (access.own & (1 << d.resource)) === 0);
  if (wanted.length === 0) return [];
  const cityTiles = new Set(citiesOf(state, civId).map((c) => cityIndex(map, c)));
  const open = (i: number): boolean => {
    const terrain = content.terrain.get(map.terrain[i]!);
    return !terrain.water && terrain.passable && (map.owner[i] === civId || map.owner[i] === NONE);
  };
  const jobs = new Set<number>();
  for (const deposit of wanted) {
    const came = new Map<number, number>([[deposit.tile, -1]]);
    let frontier = [deposit.tile];
    let reached = -1;
    for (let step = 0; step < MAX_LINK && reached < 0 && frontier.length > 0; step++) {
      const next: number[] = [];
      for (const tile of frontier) {
        for (const n of neighborIndices(map, tile)) {
          if (came.has(n) || !open(n)) continue;
          came.set(n, tile);
          if (cityTiles.has(n)) {
            reached = n;
            break;
          }
          next.push(n);
        }
        if (reached >= 0) break;
      }
      frontier = next;
    }
    if (reached < 0) continue;
    for (let tile = came.get(reached)!; tile !== -1; tile = came.get(tile)!) {
      if (map.road[tile] === 0) jobs.add(tile);
    }
  }
  return [...jobs].sort((a, b) => a - b);
}

/**
 * An automated worker of the player's chooses its own job, exactly as the AI's workers
 * do — including replacing an improvement when the city would gain by it (ADR-0077).
 * Runs when it is automated and again at the start of each turn while it is idle.
 */
export function automateWorker(state: GameState, content: Content, unit: Unit): GameEvent[] {
  if (!unit.automated || unit.movesLeft <= 0 || unit.orders !== null) return [];
  const previous = state.meta.activeCiv;
  state.meta.activeCiv = unit.owner;
  try {
    return asAutomation(() => improve(state, content, unit.owner, unit));
  } finally {
    state.meta.activeCiv = previous;
  }
}

/**
 * Give a worker its next job: the improvement that adds most to what a city of ours
 * gets from a tile, for the time it takes to walk there and build it (AI-1).
 *
 * AI-0 started on whatever tile it stood on with whatever yielded most, which meant two
 * workers could turn the same tile from plantation to grove and back for ever — it
 * built 191 improvements in 150 turns and kept four. Now an improvement is only built
 * where it is a gain over what is already there, judged by the same weights the city
 * governor works tiles by, and no two workers aim at the same tile.
 */
function improve(state: GameState, content: Content, civId: Id, unit: Unit): GameEvent[] {
  const here = unitIndex(state.map, unit);
  const ai = weightsFor(content, state.civs[civId]);
  const civ = state.civs[civId];
  if (!civ) return [];
  const w = content.rules.cities.workWeights;
  const value = (food: number, production: number, gold: number): number => food * w.food + production * w.production + gold * w.gold;

  // Tiles another of our workers is already seeing to.
  const taken = new Set<number>();
  for (const other of unitsOf(state, civId)) {
    if (other.id === unit.id || !unitDef(content, other).abilities.includes('build_improvement')) continue;
    if (other.orders?.kind === 'build') taken.add(unitIndex(state.map, other));
    if (other.orders?.kind === 'goto') taken.add(other.orders.destination);
  }

  const options = content.improvements.all.filter((i) => i.key !== 'none' && hasTech(civ, i.techId));
  let best: { tile: number; improvement: number; score: number } | null = null;
  for (const city of citiesOf(state, civId)) {
    const centre = cityIndex(state.map, city);
    const tiles = [centre, ...city.workedTiles];
    // More food is worth nothing to a city that would throw it away — but food it
    // already has is not torn up for it either, or the city would starve and want it
    // back (ADR-0069).
    const extraFood = foodIsWasted(content, city) ? content.rules.cities.capFoodWeight : w.food;
    for (const tile of tiles) {
      if (taken.has(tile)) continue;
      const distance = wrappedDistance(state.map, here, tile);
      if (distance > ai.settleSearchRadius) continue;
      const current = content.improvements.get(state.map.improvement[tile]!);
      const before = value(current.yields.food, current.yields.production, current.yields.gold);
      for (const improvement of options) {
        if (!improvementAllowed(content, state.map, tile, improvement)) continue;
        const after = value(improvement.yields.food, improvement.yields.production, improvement.yields.gold);
        // A road lies beside what is there rather than replacing it, and is worth its
        // yield plus the speed it gives (ADR-0055); anything else replaces the current
        // improvement and is worth only what it adds over it.
        const food = improvement.yields.food - current.yields.food;
        const gain = improvement.route
          ? after * ai.roadValue
          : after - before - (food > 0 ? food * (w.food - extraFood) : 0);
        if (gain <= 0) continue;
        const score = gain / (improvement.turnsToBuild + distance);
        if (!best || score > best.score) best = { tile, improvement: improvement.id, score };
      }
    }
  }

  // A strategic resource we lack lying unconnected in our land: a road to it is worth
  // more than most tiles (ADR-0063). The nearest unroaded step on the way is the job.
  const route = content.route;
  if (route && hasTech(civ, route.techId)) {
    for (const tile of resourceRoadJobs(state, content, civId)) {
      if (taken.has(tile)) continue;
      const distance = wrappedDistance(state.map, here, tile);
      if (distance > ai.settleSearchRadius) continue;
      const score = ai.resourceRoadValue / (route.turnsToBuild + distance);
      if (!best || score > best.score) best = { tile, improvement: route.id, score };
    }
  }

  if (!best) {
    const skip = apply(state, content, { t: 'SkipUnit', unitId: unit.id });
    return skip.ok ? skip.value : [];
  }
  if (best.tile === here) {
    const started = apply(state, content, { t: 'BuildImprovement', unitId: unit.id, improvement: best.improvement });
    return started.ok ? started.value : [];
  }
  const go = apply(state, content, { t: 'SetGoto', unitId: unit.id, to: best.tile });
  if (go.ok) return go.value;
  const skip = apply(state, content, { t: 'SkipUnit', unitId: unit.id });
  return skip.ok ? skip.value : [];
}

/**
 * Walk toward the unknown.
 *
 * Scores candidate destinations by how much undiscovered ground sits around them,
 * discounted by the walk. Crude, and enough: an explorer that heads for the biggest
 * patch of dark is doing the only thing exploration asks for.
 */
function explore(
  state: GameState,
  content: Content,
  civId: Id,
  unit: Unit,
  rng: RngState | undefined,
): GameEvent[] {
  const ai = weightsFor(content, state.civs[civId]);
  const vision = state.vision[civId];
  const here = unitIndex(state.map, unit);
  const def = unitDef(content, unit);
  if (!vision) return [];

  let best: number | null = null;
  let bestScore = -Infinity;

  for (let index = 0; index < tileCount(state.map); index++) {
    if (vision[index] === VIS_UNKNOWN) continue; // cannot aim at what is not known to exist
    // Land for walkers, water for ships — whatever this unit could stand on.
    if (!canEnter(content, state.map, index, def)) continue;

    const distance = wrappedDistance(state.map, here, index);
    // Ships look further: oceans are wider than valleys (ADR-0072).
    const reach = def.domain === 'sea' ? ai.exploreSearchRadius * ai.seaExploreReach : ai.exploreSearchRadius;
    if (distance === 0 || distance > reach) continue;

    const score = darknessAround(state, vision, index) - distance * ai.exploreDistancePenalty;
    if (score > bestScore) {
      bestScore = score;
      best = index;
    }
  }

  if (best === null || bestScore <= 0) {
    // Nothing left worth walking to. Fortify rather than skip, so a warrior that has
    // run out of map becomes a garrison instead of a wanderer.
    const hold = apply(state, content, { t: 'Fortify', unitId: unit.id });
    return hold.ok ? hold.value : [];
  }

  // A little jitter so several units do not all pile toward the same frontier.
  if (rng && nextInt(rng, 0, 4) === 0) {
    const wander = randomKnownTile(state, content, vision, rng, def);
    if (wander !== null) best = wander;
  }

  const go = apply(state, content, { t: 'SetGoto', unitId: unit.id, to: best });
  return go.ok ? go.value : [];
}

/** How much unknown ground sits next to a tile. */
function darknessAround(state: GameState, vision: Uint8Array, index: number): number {
  const { col, row } = indexToOffset(state.map, index);
  let dark = 0;
  for (let dr = -2; dr <= 2; dr++) {
    for (let dc = -2; dc <= 2; dc++) {
      const r = row + dr;
      if (r < 0 || r >= state.map.height) continue;
      const c = ((col + dc) % state.map.width + state.map.width) % state.map.width;
      if (vision[r * state.map.width + c] === VIS_UNKNOWN) dark++;
    }
  }
  return dark;
}

function randomKnownTile(state: GameState, content: Content, vision: Uint8Array, rng: RngState, def: UnitDef): number | null {
  const candidates: number[] = [];
  for (let index = 0; index < tileCount(state.map); index++) {
    if (vision[index] === VIS_UNKNOWN) continue;
    if (canEnter(content, state.map, index, def)) candidates.push(index);
  }
  return candidates.length === 0 ? null : candidates[nextInt(rng, 0, candidates.length - 1)]!;
}

// ─── cities ────────────────────────────────────────────────────────────────────

/**
 * Decide what a city builds, when it is building nothing (or is stuck).
 *
 * Scored, with the weights in data. The ordering it produces — settlers early, then
 * workers, then infrastructure — comes out of the numbers rather than being written
 * down as a plan, which is the point: AI-1 changes the weights, not the code.
 */
function directCity(state: GameState, content: Content, cityId: Id, front: Front | null = null): GameEvent[] {
  const city = state.cities[cityId];
  if (!city) return [];
  // Busy — unless what it is building waits on a strategic resource it has lost, which
  // may never come back: then it chooses again (ADR-0063).
  const head = city.queue[0];
  if (head && resourceBlocker(state, content, city, head) === -1) return [];
  if (inDisorder(state, content, city)) return [];

  const ai = weightsFor(content, state.civs[city.owner]);
  const owned = citiesOf(state, city.owner);
  const settlers = unitsOf(state, city.owner).filter((u) =>
    unitDef(content, u).abilities.includes('found_city'),
  ).length;
  const workers = unitsOf(state, city.owner).filter((u) =>
    unitDef(content, u).abilities.includes('build_improvement'),
  ).length;
  const fighting = unitsOf(state, city.owner).filter((u) => {
    const def = unitDef(content, u);
    return def.domain === 'land' && (canAttack(def) || canDefend(def));
  }).length;
  const siegeEngines = unitsOf(state, city.owner).filter((u) => isSiege(unitDef(content, u))).length;
  const aircraft = unitsOf(state, city.owner).filter((u) => unitDef(content, u).domain === 'air').length;
  const interceptors = unitsOf(state, city.owner).filter((u) => unitDef(content, u).abilities.includes('intercept')).length;
  const enemyFlies = enemiesOf(state, city.owner).some((e) => unitsOf(state, e).some((u) => unitDef(content, u).domain === 'air'));
  const atWar = enemiesOf(state, city.owner).length > 0;
  const wondersHeld = owned.reduce((n, c) => n + c.buildings.filter((b) => content.buildings.get(b).wonder).length, 0);
  const unmet = contactsOf(state, city.owner).length < rivalsOf(state, city.owner).length;
  const preparing = !atWar && arming(state, content, city.owner);
  const overseas = atWar ? overseasTarget(state, content, city.owner) : null;
  const here = cityIndex(state.map, city);
  // Short of the defenders its threat calls for — none at all, or fewer than the enemy
  // soldiers nearby ask for (AI-1's influence map, in garrisonWant).
  const guards = unitsOf(state, city.owner).filter(
    (u) => unitIndex(state.map, u) === here && canDefend(unitDef(content, u)) && unitDef(content, u).domain === 'land',
  ).length;
  const unguarded = guards < garrisonWant(state, content, city.owner, city);
  // Somewhere left to settle, within reach of this city; without one, no settlers.
  const roomToGrow = bestSiteNear(state, content, city.owner, here, NONE, true) !== null;

  let best: ProductionItem | null = null;
  let bestScore = -Infinity;

  const civ = state.civs[city.owner];
  if (!civ) return [];
  // What the books look like with no science at all: the most gold this civilisation
  // can make. Building something whose upkeep that cannot carry only means selling it
  // again — measured, AI-0 was selling a building every few turns (ADR-0050).
  const bestNet = budget(state, content, city.owner, 0).net;
  let access: ResourceAccess | undefined;
  for (const item of buildableItems(content, city, civ, state.map)) {
    if (buildBlocker(city, item) || wonderBlocker(state, content, city, item)) continue;
    // Soldiers that need a strategic resource this city lacks are not on the list (ADR-0063).
    if (item.kind === 'unit' && content.units.get(item.type).resourceIds.length > 0) {
      access ??= accessFor(state, content, city.owner);
      if (resourceBlocker(state, content, city, item, access) !== -1) continue;
    }

    let score = 0;
    if (item.kind === 'unit') {
      const def = content.units.get(item.type);
      if (def.abilities.includes('found_city')) {
        // Expansion is the strongest early move and tails off as the empire fills.
        score = roomToGrow
          ? ai.settlerValue - owned.length * ai.settlerPerCityPenalty - settlers * ai.settlerPerPendingPenalty
          : -Infinity;
        if (city.population <= def.populationCost) score = -Infinity;
      } else if (def.abilities.includes('build_improvement')) {
        // A worker per city or so: worth more the bigger the empire it has to tend.
        score = ai.workerValue - workers * ai.workerPerWorkerPenalty + owned.length * ai.workerPerCityBonus;
      } else if (def.airCapacity > 0) {
        // A carrier for an overseas war, once there are planes to put on it (ADR-0066).
        // Afloat or on the stocks: one is enough.
        const decks =
          unitsOf(state, city.owner).filter((u) => unitDef(content, u).airCapacity > 0).length +
          owned.filter((c) => c.queue.some((q) => q.kind === 'unit' && content.units.get(q.type).airCapacity > 0)).length;
        score = atWar && overseas !== null && aircraft > 0 && decks === 0 ? ai.carrierValue : -Infinity;
      } else if (def.domain === 'sea') {
        // AI-0's navy: one ship to look around, and a carrier when a war needs one.
        const ships = unitsOf(state, city.owner).filter((u) => unitDef(content, u).domain === 'sea');
        const carriers = ships.filter((u) => unitDef(content, u).capacity > 0).length;
        if (ships.length === 0) score = ai.seaScoutValue;
        // Rivals still unmet and no ship that can cross an ocean: build one to look
        // for them (ADR-0072). Coastal ships cannot leave sight of land.
        else if (!def.coastal && def.capacity === 0 && unmet && !ships.some((u) => !unitDef(content, u).coastal)) score = ai.seaScoutValue;
        else if (def.capacity > 0 && carriers === 0 && overseas !== null) score = ai.transportValue + def.capacity * 4;
        // A planned invasion short of room aboard (ADR-0072): ships that can make the crossing.
        else if (def.capacity > 0 && front?.overseas && front.needLift > 0 && !def.coastal) score = ai.transportValue + def.capacity * 4 + ai.armyNeedBonus;
        else if (def.capacity === 0 && canAttack(def)) {
          // Warships (ADR-0067): one for every transport or carrier to guard, and a home
          // squadron in wartime. Counting those already on the stocks.
          const isWarship = (d: UnitDef): boolean => d.domain === 'sea' && d.capacity === 0 && d.airCapacity === 0 && canAttack(d);
          const warships =
            ships.filter((u) => isWarship(unitDef(content, u))).length +
            owned.filter((c) => c.queue.some((q) => q.kind === 'unit' && isWarship(content.units.get(q.type)))).length;
          const convoys = ships.filter((u) => unitDef(content, u).capacity > 0 || unitDef(content, u).airCapacity > 0).length;
          const want = convoys + (atWar ? ai.homeFleet : 0);
          score = warships < want ? ai.warshipValue + def.attack * ai.unitStrengthWeight + (atWar ? ai.warUnitBonus / 2 : 0) : -Infinity;
        }
        else score = -Infinity;
      } else if (canAttack(def) || canDefend(def)) {
        // An army is worth more the fewer soldiers there are, the stronger this one is,
        // and the more so in wartime.
        score =
          ai.defenderValue -
          fighting * ai.defenderPerUnitPenalty +
          Math.max(def.attack, def.defence) * ai.unitStrengthWeight +
          (atWar ? ai.warUnitBonus : 0) +
          (preparing && canAttack(def) && def.domain === 'land' ? ai.warPrepBonus : 0) +
          // What the war plan is short of (ADR-0072).
          (front && isArmyType(def) && (isSiege(def) ? front.needSiege > 0 : front.needSoldiers > 0) ? ai.armyNeedBonus : 0) +
          (unguarded ? ai.unguardedBonus : 0);
        // Siege engines break cities but cannot hold them: a share of the army, and
        // mostly in wartime (ADR-0064).
        // Aircraft: a share of the army, for wartime (ADR-0065).
        if (def.domain === 'air') {
          // Valued on their own terms: an air force is not more of the same army.
          score =
            ai.airValue -
            aircraft * ai.airPerUnitPenalty +
            Math.max(def.attack, def.defence) * ai.unitStrengthWeight +
            (atWar ? ai.warUnitBonus : 0) +
            // Against an enemy that flies, fighters to meet it: about one for every three cities.
            (enemyFlies && def.abilities.includes('intercept') && interceptors < Math.ceil(owned.length / 3) ? ai.interceptorBonus : 0);
          if (aircraft >= Math.max(1, Math.floor(fighting * ai.airShare))) score = -Infinity;
          else if (!atWar) score -= ai.airPeacePenalty;
          if (unguarded) score = -Infinity;
        }
        if (isSiege(def)) {
          const wanted = front !== null && front.needSiege > 0;
          if (!wanted && siegeEngines >= Math.max(1, Math.floor(fighting * ai.siegeShare))) score = -Infinity;
          else if (!atWar) score -= ai.siegePeacePenalty;
          if (unguarded) score = -Infinity;
        }
      } else {
        const scouts = unitsOf(state, city.owner).filter((u) => {
          const d = unitDef(content, u);
          return d.domain === 'land' && !canAttack(d) && !canDefend(d) && d.abilities.length > 0 &&
            !d.abilities.includes('found_city') && !d.abilities.includes('build_improvement');
        }).length;
        score = ai.scoutValue - scouts * ai.scoutPerScoutPenalty;
      }
    } else {
      const def = content.buildings.get(item.building);
      // Buildings are valued by what they do, cheaply approximated.
      score =
        ai.buildingBase +
        (def.effects.growthFoodKept ?? 0) * ai.buildingGrowthWeight +
        (def.effects.productionBonus ?? 0) * ai.buildingProductionWeight +
        (def.effects.goldBonus ?? 0) * ai.buildingGoldWeight +
        (def.effects.contentment ?? 0) * ai.buildingContentmentWeight +
        (def.effects.sizeCapIncrease ?? 0) * ai.buildingSizeCapWeight +
        (def.effects.dragReduction ?? 0) * ai.buildingDragWeight +
        (def.effects.sciencePercent ?? 0) * ai.buildingScienceWeight +
        (def.effects.veteranUnits ? ai.buildingVeteranWeight : 0) +
        (def.effects.wallsDefence ?? 0) * ai.buildingWallsWeight * (atWar || unguarded ? 1 : ai.wallsPeaceShare) +
        (def.effects.antiAir ?? 0) * ai.buildingAntiAirWeight * (enemyFlies ? 1 : ai.wallsPeaceShare) +
        (def.effects.culture ?? 0) * ai.buildingCultureWeight;
      // A wonder's reach over every city is valued as that effect in each of them, at a
      // discount for the risk of being beaten to it (ADR-0057).
      const reach = def.civEffects;
      if (Object.keys(reach).length > 0) {
        score +=
          owned.length *
          ai.wonderCivShare *
          ((reach.contentment ?? 0) * ai.buildingContentmentWeight +
            (reach.productionBonus ?? 0) * ai.buildingProductionWeight +
            (reach.goldBonus ?? 0) * ai.buildingGoldWeight +
            (reach.sciencePercent ?? 0) * ai.buildingScienceWeight +
            (reach.culture ?? 0) * ai.buildingCultureWeight +
            (reach.veteranUnits ? ai.buildingVeteranWeight / 2 : 0));
      }
      // Each wonder already held makes the next less tempting, so one civilisation does
      // not take them all (ADR-0073).
      if (def.wonder) score -= wondersHeld * ai.wonderPerHeldPenalty;
    }

    // Everything is discounted by what it costs, so a poor city builds cheap things —
    // a wonder less steeply, and worth wonderBase for itself: there is only one, and the
    // later ones cost several times the early ones (ADR-0073).
    const wonder = item.kind === 'building' && content.buildings.get(item.building).wonder;
    score -= itemCost(content, item, civ) * (wonder ? ai.wonderCostPenalty : ai.costPenalty);
    if (wonder) score += ai.wonderBase;

    if (item.kind === 'building') {
      const def = content.buildings.get(item.building);
      const pays = (def.effects.goldBonus ?? 0) > 0;
      if (def.upkeep > 0 && bestNet - def.upkeep < 0 && !pays) score = -Infinity;
    }

    // Ties break by content order, which is stable, so the AI is reproducible.
    if (score > bestScore) {
      bestScore = score;
      best = item;
    }
  }

  // Nothing worth its cost: wait, and let the shields build up for when something is.
  // Without this, a city that could afford no building trained settler after settler
  // at a loss, simply because a negative score still beat none.
  if (!best || bestScore <= 0) return [];
  const result = apply(state, content, { t: 'SetProduction', cityId: city.id, item: best });
  return result.ok ? result.value : [];
}
